import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { fetchFollowedBuildings } from '../store/buildings-actions';
import { Building } from '../models/data';
import navClasses from './BottomNav.module.scss';
import classes from './QuickAdd.module.scss';

// Calendar slot in the bottom nav. If the user follows a single property we
// jump straight to that property's /events feed; with two or more followed we
// open the same style of picker used for "Create a post" so they can choose
// which calendar to open (default is highlighted).

const buildingUrl = (b: Building | { id: string; plusCode?: string; addressSlug?: string } | undefined | null) => {
  if (!b) return null;
  const id = (b as any).addressSlug || ((b as any).plusCode ? (b as any).plusCode.replace(/\+/g, '-') : b.id);
  return `/building/${id}`;
};

const initial = (n?: string) => (n?.trim()?.[0] || '?').toUpperCase();

const CalendarIcon = () => (
  <svg className={navClasses.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18" />
    <path d="M8 3v4M16 3v4" />
  </svg>
);

const EventsNavButton = () => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((s: RootState) => s.user.user);
  const followed = useSelector((s: RootState) => s.buildings.followedBuildings);
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const active = location.pathname.endsWith('/events') || location.pathname === '/calendar';

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  useEffect(() => {
    if (open && user?.id) dispatch(fetchFollowedBuildings(user.id));
  }, [open, user?.id, dispatch]);

  const eventsAt = (b: Building) => {
    const url = buildingUrl(b);
    if (!url) return;
    setOpen(false);
    navigate(`${url}/events`);
  };

  const handleClick = () => {
    if (followed.length === 0) {
      navigate('/my?add=1');
      return;
    }
    if (followed.length === 1) {
      eventsAt(followed[0]);
      return;
    }
    setOpen(v => !v);
  };

  const defaultBuildingId = user.building?.id;
  const defaultInFollowed = defaultBuildingId
    ? followed.find(b => b.id === defaultBuildingId)
    : null;
  const others = followed.filter(b => b.id !== defaultBuildingId);

  return (
    <div className={classes.wrap} ref={wrapRef}>
      <button
        type="button"
        className={`${navClasses.slot} ${active ? navClasses.active : ''}`}
        onClick={handleClick}
        aria-expanded={open || undefined}
        aria-haspopup={followed.length > 1 ? 'menu' : undefined}
        aria-label="Events"
        aria-current={active ? 'page' : undefined}
      >
        <CalendarIcon />
      </button>
      {open && followed.length > 1 && (
        <div className={classes.menu} role="menu" aria-label="Choose a calendar view">
          <div className={classes.pickHeader}>
            <span className={classes.pickTitle}>Open events at…</span>
          </div>
          <button
            type="button"
            className={classes.pickItem}
            role="menuitem"
            onClick={() => { setOpen(false); navigate('/calendar'); }}
          >
            <span className={classes.pickItem__avatar} aria-hidden="true">📅</span>
            <span className={classes.pickItem__body}>
              <span className={classes.pickItem__name}>All events</span>
              <span className={classes.pickItem__meta}>Everything across your properties</span>
            </span>
          </button>
          {defaultInFollowed && (
            <button
              type="button"
              className={classes.pickItem}
              role="menuitem"
              onClick={() => eventsAt(defaultInFollowed)}
            >
              <span className={classes.pickItem__avatar} aria-hidden="true">
                {initial(defaultInFollowed.name)}
              </span>
              <span className={classes.pickItem__body}>
                <span className={classes.pickItem__name}>{defaultInFollowed.name}</span>
                <span className={classes.pickItem__meta}>★ Default</span>
              </span>
            </button>
          )}
          {others.map(b => (
            <button
              type="button"
              key={b.id}
              className={classes.pickItem}
              role="menuitem"
              onClick={() => eventsAt(b)}
            >
              <span className={classes.pickItem__avatar} aria-hidden="true">
                {initial(b.name)}
              </span>
              <span className={classes.pickItem__body}>
                <span className={classes.pickItem__name}>{b.name}</span>
                {b.displayAddress && (
                  <span className={classes.pickItem__meta}>{b.displayAddress}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventsNavButton;
